import React from "react";
import { useUser, useSession } from "@clerk/clerk-react";
import { SignOutButton } from "@clerk/clerk-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Settings, LogOut } from "lucide-react";
import { format } from "date-fns";
import { useSafeWindowManager } from "@/hooks/useSafeWindowManager";
import { useResponsive } from "@/hooks/use-responsive";
import AccountManagementWindow from "@/components/windows/AccountManagementWindow";

interface AuthStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuthStatusDialog({ open, onOpenChange }: AuthStatusDialogProps) {
  const { user } = useUser();
  const { session } = useSession();
  const { windowManager } = useSafeWindowManager();
  const { openWindow, closeWindow } = windowManager;
  const { isMobile, isTablet, width, height } = useResponsive();

  if (!user || !session) {
    return null;
  }

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return "N/A";
    return format(new Date(date), "MMM dd, yyyy 'at' h:mm a");
  };

  const handleOpenAccountManagement = () => {
    // More reasonable window sizing
    const getWindowSize = () => {
      if (isMobile) {
        return { width: width - 20, height: height - 40 };
      } else if (isTablet) {
        return { width: Math.min(700, width - 40), height: Math.min(600, height - 60) };
      } else {
        return { width: Math.min(800, width * 0.7), height: Math.min(650, height * 0.8) };
      }
    };

    openWindow(
      'account-management',
      'Account Management',
      <AccountManagementWindow onClose={() => closeWindow('account-management')} />,
      {
        appType: 'account-management',
        size: getWindowSize()
      }
    );
    onOpenChange(false); // Close the dialog after opening the window
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm bg-black/40 backdrop-blur-2xl border border-white/20 shadow-2xl rounded-2xl p-0 overflow-hidden">
        <div className="relative">
          {/* Elegant header with gradient */}
          <div className="bg-gradient-to-r from-white/10 to-white/5 border-b border-white/10 px-6 py-4">
            <DialogTitle className="flex items-center justify-between text-lg font-medium text-white">
              <span className="flex items-center gap-3">
                Authentication
                <Badge variant="secondary" className="bg-green-500/20 text-green-400 border-green-500/30 text-xs px-2 py-0.5">
                  Active
                </Badge>
              </span>
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm mt-1">
              Session authenticated
            </DialogDescription>
          </div>
          
          {/* Compact content */}
          <div className="px-6 py-5 space-y-4">
            {/* Essential user info only */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wide">Email</span>
                <span className="text-white text-sm font-medium">{user.primaryEmailAddress?.emailAddress || "No email"}</span>
              </div>
              
              {user.fullName && (
                <div className="flex justify-between items-center">
                  <span className="text-white/60 text-xs font-medium uppercase tracking-wide">Name</span>
                  <span className="text-white text-sm font-medium">{user.fullName}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wide">Since</span>
                <span className="text-white text-sm font-medium">{format(new Date(user.createdAt || new Date()), "MMM yyyy")}</span>
              </div>
            </div>

            <Separator className="bg-white/10" />

            {/* Elegant action buttons */}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 h-9 text-xs font-medium"
                onClick={handleOpenAccountManagement}
              >
                <Settings className="w-3 h-3 mr-2" />
                Settings
              </Button>
              
              <SignOutButton>
                <Button variant="destructive" size="sm" className="flex-1 h-9 text-xs font-medium">
                  <LogOut className="w-3 h-3 mr-2" />
                  Sign Out
                </Button>
              </SignOutButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}